import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Headphones,
  Play,
  Pause,
  Clock,
  Sparkles,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';
import { useAudioPlayer } from '@/contexts/AudioPlayerContext';
import useUserUuid from './useUserUuid';
import { getGuestTourIds } from '@/lib/guestPurchases';

import { getBackendUrl } from '../../lib/runtimeConfig';

const API = `${getBackendUrl()}/api`;

/**
 * Region detection: scans the user's submitted query + every result
 * address for known wine-region tokens and returns the canonical
 * region name (matches the `region` column in `audio_tours`). Kept
 * deterministic on purpose — no LLM call just to figure out where
 * the trip is.
 *
 * Add a new region by extending REGION_KEYWORDS; the matcher returns
 * the first hit, so list them most-specific → least-specific.
 */
const REGION_KEYWORDS = [
  {
    region: 'Napa Valley',
    patterns: [
      /\bnapa\b/i,
      /\bcalistoga\b/i,
      /\boakville\b/i,
      /\bst\.?\s*helena\b/i,
      /\byountville\b/i,
      /\brutherford\b/i,
    ],
  },
  {
    region: 'Sonoma',
    patterns: [
      /\bsonoma\b/i,
      /\bhealdsburg\b/i,
      /\bdry\s*creek\b/i,
      /\brussian\s*river\b/i,
      /\balexander\s*valley\b/i,
    ],
  },
  {
    region: 'Willamette Valley',
    patterns: [/\bwillamette\b/i, /\bdundee\b/i, /\bmcminnville\b/i],
  },
  {
    region: 'Paso Robles',
    patterns: [/\bpaso\s*robles\b/i, /\bcentral\s*coast\b/i],
  },
];

const detectRegion = (query, results) => {
  const haystack = [query || '', ...(results || []).map((r) => r.address || '')]
    .join(' ')
    .slice(0, 2000); // bound the scan
  for (const { region, patterns } of REGION_KEYWORDS) {
    if (patterns.some((p) => p.test(haystack))) return region;
  }
  return null;
};

const formatPrice = (price) => `$${Number(price || 0).toFixed(2)}`;

const resolveImage = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Relative API paths (e.g. /api/files/abc) need the backend prefix.
  if (url.startsWith('/api/')) return `${getBackendUrl()}${url}`;
  return url;
};

/**
 * AudioTourUpsell — revenue-driving recommendation card that surfaces
 * relevant Audio Tours after the AI Concierge generates an itinerary.
 *
 * Behaviour:
 *   - Detects the trip region from the query + addresses.
 *   - Fetches up to 2 matching tours (region-filtered, falls back to
 *     featured tours if the region has none).
 *   - Renders a dark, wine-themed card under the itinerary results with:
 *       · 30-second preview (uses the global AudioPlayer context)
 *       · "Add" CTA that routes to the embedded checkout
 *       · "Purchased" badge + "Play" button if the customer already
 *         purchased the tour (avoids upselling them twice).
 *   - Fires impression / preview / click beacons to the backend so we
 *     can measure the conversion funnel from Admin AI Analytics.
 *
 * Mental model: this is the post-itinerary upsell — the user has just
 * received a beautiful 3-stop plan and is at peak intent. We don't
 * interrupt the flow; we sit BELOW the Save & Share bar as a calm
 * "make this even better" prompt.
 */
const AudioTourUpsell = ({ submittedQuery, results }) => {
  const [tours, setTours] = useState([]);
  const [purchasedIds, setPurchasedIds] = useState([]);
  const navigate = useNavigate();
  const { playTrack, currentTrack, isPlaying, togglePlayPause } = useAudioPlayer();
  const { uuid } = useUserUuid();
  const impressionFiredRef = useRef('');

  // Stable signature of the itinerary's address list. We use this — rather
  // than `results` itself — as the effect dependency because `results` gets
  // a brand-new array reference every time the parent hook patches in a
  // geocoded lat/lon (typically 3-4 times in the first 5 seconds). Without
  // this stabilisation the upsell would unmount → refetch → remount on each
  // geocode tick, producing the "flicker" the user reported.
  const resultsKey = useMemo(
    () =>
      (results || [])
        .map((r) => `${r?.name || ''}|${r?.address || ''}`)
        .join('::'),
    [results],
  );

  const detectedRegion = useMemo(
    () => detectRegion(submittedQuery, results),
    // We deliberately depend on resultsKey instead of results so the
    // detection doesn't re-run when geocoding patches in lat/lon.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [submittedQuery, resultsKey],
  );

  // Load purchased-tour ids so we can swap "Add" for "Listen" on
  // tours the user already owns. Best-effort — failure just means
  // we show "Add" everywhere, which is the safer default.
  //
  // Merges two sources:
  //   1. localStorage guest purchases (works without login)
  //   2. Server-side customer purchases (only if logged in)
  useEffect(() => {
    // 1) Hydrate guest purchases from localStorage immediately (no fetch)
    const guestIds = getGuestTourIds();
    if (guestIds.length > 0) {
      setPurchasedIds((prev) => Array.from(new Set([...prev, ...guestIds])));
    }
    // 2) Merge in server-side customer purchases if logged in
    const token = localStorage.getItem('customer_token');
    if (!token) return;
    let cancelled = false;
    axios
      .get(`${API}/audio-tours/purchased`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000,
      })
      .then((res) => {
        if (cancelled) return;
        const serverIds = Array.isArray(res.data) ? res.data.map((t) => t.tour_id) : [];
        setPurchasedIds((prev) => Array.from(new Set([...prev, ...serverIds])));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Tour lookup — region-first, with a graceful fallback to the
  // unfiltered list so the upsell still renders even when the user's
  // region doesn't yet have a dedicated tour. We deliberately DO NOT
  // clear `tours` while a refetch is in flight: the user sees the
  // previous matches stay put until the new ones land, preventing the
  // "card disappears then reappears" flicker on every geocode tick.
  useEffect(() => {
    if (!resultsKey) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const params = detectedRegion
          ? { region: detectedRegion, limit: 4 }
          : { limit: 4 };
        const res = await axios.get(`${API}/audio-tours`, { params, timeout: 8000 });
        let data = Array.isArray(res.data) ? res.data : [];
        if (data.length === 0 && detectedRegion) {
          const fallback = await axios.get(`${API}/audio-tours`, {
            params: { limit: 4 },
            timeout: 8000,
          });
          data = Array.isArray(fallback.data) ? fallback.data : [];
        }
        if (!cancelled) setTours(data.slice(0, 2));
      } catch {
        // Keep existing tours visible on transient network errors —
        // wiping them would just retrigger the flicker we're guarding
        // against. The user's session is more important than a
        // perfectly-fresh upsell list.
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [detectedRegion, resultsKey]);

  // Fire one impression beacon per distinct tour-set so we can measure
  // upsell conversion (impressions → previews → clicks) in admin
  // analytics. Fire-and-forget; failures are intentionally silent.
  useEffect(() => {
    if (!tours.length) return;
    const key = tours.map((t) => t.tour_id).join('|');
    if (impressionFiredRef.current === key) return;
    impressionFiredRef.current = key;
    axios
      .post(
        `${API}/ai-search/upsell-event`,
        {
          event: 'impression',
          tour_ids: tours.map((t) => t.tour_id),
          region: detectedRegion,
          query: submittedQuery,
          user_uuid: uuid,
        },
        { timeout: 5000 },
      )
      .catch(() => {});
  }, [tours, detectedRegion, submittedQuery, uuid]);

  const handlePreview = (tour) => {
    const previewUrl = tour.preview_url || tour.audio_url;
    if (!previewUrl) return;
    const isOwned = purchasedIds.includes(tour.tour_id);
    const isCurrent = currentTrack?.tour_id === tour.tour_id;
    if (isCurrent) {
      togglePlayPause();
      return;
    }
    playTrack({
      ...tour,
      audio_url: isOwned ? tour.audio_url : previewUrl,
      is_preview: !isOwned,
    });
    axios
      .post(
        `${API}/ai-search/upsell-event`,
        {
          event: 'preview',
          tour_id: tour.tour_id,
          region: detectedRegion,
          user_uuid: uuid,
        },
        { timeout: 5000 },
      )
      .catch(() => {});
  };

  const handleAdd = (tour) => {
    axios
      .post(
        `${API}/ai-search/upsell-event`,
        {
          event: 'click',
          tour_id: tour.tour_id,
          region: detectedRegion,
          user_uuid: uuid,
        },
        { timeout: 5000 },
      )
      .catch(() => {});
    // Guest checkout — no auth required. The /checkout page handles
    // logged-in users transparently (it sends the JWT if present so the
    // backend pre-links the purchase to the user account).
    navigate(`/checkout/${tour.tour_id}`);
  };

  if (!tours || tours.length === 0) return null;

  return (
    <section
      className="rounded-2xl bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white p-4 sm:p-5 ring-1 ring-white/10 shadow-lg"
      data-testid="audio-tour-upsell"
    >
      <header className="flex items-start gap-3 mb-4">
        <div className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center shadow-md">
          <Headphones className="w-4 h-4 text-[#1a1a2e]" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gold-300 mb-0.5">
            <Sparkles className="w-3 h-3 text-gold-400" />
            Make the drive unforgettable
          </div>
          <h3 className="font-serif text-base sm:text-lg leading-tight">
            {detectedRegion
              ? `Add an immersive audio tour for ${detectedRegion}`
              : 'Add an immersive audio tour to your trip'}
          </h3>
          <p className="text-white/60 text-xs sm:text-[13px] mt-0.5 leading-snug">
            Sommelier-narrated stories you can stream the whole drive. 30-second previews on us.
          </p>
        </div>
      </header>

      <div className="space-y-2.5">
        {tours.map((t) => {
          const isOwned = purchasedIds.includes(t.tour_id);
          const isCurrent = currentTrack?.tour_id === t.tour_id;
          const isPlayingThis = isCurrent && isPlaying;
          const previewable = Boolean(t.preview_url || t.audio_url);
          return (
            <article
              key={t.tour_id}
              /* Mobile: 2-row layout (title row → price+buttons row) so
                 long winery names like "Castello di Amorosa" and "Napa
                 Valley audio tour" have full card width to render.
                 Desktop (sm:+): original 3-column horizontal flex. */
              className="rounded-xl bg-white/[0.06] hover:bg-white/[0.1] border border-white/10 p-2.5 transition-colors flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
              data-testid={`audio-tour-upsell-card-${t.tour_id}`}
            >
              {/* Row 1 on mobile / left column on desktop: thumb + title.
                  Wrapped in a Link so clicking the thumb or title lands
                  the user on the full detail page. The right-side action
                  buttons remain OUTSIDE the Link so they stay independent
                  and keep valid HTML (buttons must not nest inside <a>). */}
              <Link
                to={`/audio-tours/${t.tour_id}`}
                className="flex items-center gap-3 min-w-0 flex-1 group focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/60 rounded-lg -m-1 p-1"
                data-testid={`audio-tour-upsell-link-${t.tour_id}`}
              >
                <div className="shrink-0 w-14 h-14 rounded-lg bg-gradient-to-br from-wine-700 to-wine-900 overflow-hidden flex items-center justify-center">
                  {t.image_url || t.cover_image_url ? (
                    <img
                      src={resolveImage(t.image_url || t.cover_image_url)}
                      alt={t.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <Headphones className="w-5 h-5 text-gold-300" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-1.5 flex-wrap">
                    {/* Title wrapping strategy:
                        - Mobile: `break-words` + default white-space-normal
                          lets long titles like "Castello di Amorosa
                          Cabernet Tour" flow to 2-3 lines instead of
                          truncating mid-word. We deliberately avoid
                          `line-clamp-2` here because Tailwind's CSS
                          compilation occasionally drops it when not used
                          elsewhere in the page bundle.
                        - Desktop (`sm:+`): `sm:truncate` restores the
                          single-line ellipsis behavior since the 3-column
                          horizontal flex layout always has plenty of room.
                        The `block` display + `break-words` combination is
                        the most CSS-compatible way to force word wrap
                        across all browsers and Tailwind versions. */}
                    <div
                      className="block text-sm font-semibold text-white break-words sm:whitespace-nowrap sm:overflow-hidden sm:text-ellipsis leading-snug flex-1"
                      title={t.title}
                    >
                      {t.title}
                    </div>
                    {isOwned && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-300 shrink-0 mt-0.5">
                        <CheckCircle className="w-2.5 h-2.5" /> Purchased
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-white/50 flex items-center gap-2 mt-0.5 min-w-0">
                    {t.duration_minutes ? (
                      <span className="inline-flex items-center gap-0.5 shrink-0">
                        <Clock className="w-2.5 h-2.5" /> {t.duration_minutes} min
                      </span>
                    ) : null}
                    {t.winery_name ? (
                      <span className="truncate">· {t.winery_name}</span>
                    ) : null}
                  </div>
                </div>
              </Link>
              {/* Row 2 on mobile / right column on desktop: price + buttons.
                  On mobile we go full-width and right-align the price/buttons
                  cluster; on desktop we collapse back to a tight column. */}
              <div className="shrink-0 flex items-center justify-between sm:justify-end sm:flex-col sm:items-end gap-2 sm:gap-1 pl-[68px] sm:pl-0">
                <div className="text-sm font-bold text-gold-300">{formatPrice(t.price)}</div>
                <div className="flex items-center gap-1.5">
                  {previewable && !isOwned && (
                    <button
                      type="button"
                      onClick={() => handlePreview(t)}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold text-white/80 hover:text-white bg-white/5 hover:bg-white/10 border border-white/15 px-2 py-1 rounded-md transition-colors"
                      data-testid={`audio-tour-upsell-preview-${t.tour_id}`}
                      aria-label={isPlayingThis ? `Pause preview of ${t.title}` : `Preview ${t.title}`}
                    >
                      {isPlayingThis ? (
                        <Pause className="w-2.5 h-2.5" />
                      ) : (
                        <Play className="w-2.5 h-2.5" />
                      )}
                      {isPlayingThis ? 'Playing' : 'Preview'}
                    </button>
                  )}
                  {isOwned ? (
                    <button
                      type="button"
                      onClick={() => handlePreview(t)}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#1a1a2e] bg-emerald-300 hover:bg-emerald-200 px-2 py-1 rounded-md transition-colors"
                      data-testid={`audio-tour-upsell-play-${t.tour_id}`}
                      aria-label={isPlayingThis ? `Pause ${t.title}` : `Play ${t.title}`}
                    >
                      {isPlayingThis ? (
                        <Pause className="w-2.5 h-2.5" />
                      ) : (
                        <Play className="w-2.5 h-2.5" />
                      )}
                      {isPlayingThis ? 'Playing' : 'Play'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleAdd(t)}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#1a1a2e] bg-gold-300 hover:bg-gold-200 px-2 py-1 rounded-md transition-colors"
                      data-testid={`audio-tour-upsell-add-${t.tour_id}`}
                    >
                      Buy <ArrowRight className="w-2.5 h-2.5" />
                    </button>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => navigate('/audio-tours')}
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-medium text-gold-300 hover:text-gold-200 transition-colors"
        data-testid="audio-tour-upsell-browse-all"
      >
        Browse all audio tours <ArrowRight className="w-3 h-3" />
      </button>
    </section>
  );
};

export default AudioTourUpsell;
